import React, { useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import CloseIcon from './icons/CloseIcon';

const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.modalOverlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: `${theme.spacing.lg}px`,
  },
  modalContainer: {
    width: '100%',
    maxWidth: '500px',
    maxHeight: '85vh',
    borderRadius: `${theme.radii.xl}px`,
    backgroundColor: theme.colors.modalBackground,
    boxShadow: theme.shadows.modal,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  header: {
    padding: `${theme.spacing.xl}px ${theme.spacing.lg}px ${theme.spacing.md}px`,
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  },
  title: {
    fontSize: `${theme.typography.sizes.xl}px`,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    textAlign: 'center' as const,
  },
  closeButton: {
    position: 'absolute' as const,
    top: `${theme.spacing.lg}px`,
    right: `${theme.spacing.xl}px`,
    width: '32px',
    height: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    zIndex: 10,
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: `${theme.spacing.lg}px ${theme.spacing.xl}px`,
  },
  section: {
    marginBottom: `${theme.spacing.lg}px`,
  },
  sectionTitle: {
    fontSize: `${theme.typography.sizes.lg}px`,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    marginBottom: `${theme.spacing.sm}px`,
  },
  text: {
    fontSize: `${theme.typography.sizes.md}px`,
    color: theme.colors.textSecondary,
    lineHeight: theme.typography.lineHeights.relaxed,
    marginBottom: `${theme.spacing.sm}px`,
  },
  link: {
    color: theme.colors.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  email: {
    color: theme.colors.primary,
    fontWeight: theme.typography.weights.medium,
    cursor: 'pointer',
  },
});

interface InfoModalProps {
  visible: boolean;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({
  visible,
  onClose,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const handlePrivacyPolicyPress = () => {
    window.open('https://chordbaker.com/privacy', '_blank');
  };

  const handleEmailPress = () => {
    window.location.href = 'mailto:tomas@chordbaker.com';
  };

  if (!visible) return null;

  return (
    <div 
      style={styles.overlay}
      onClick={onClose}
    >
      <div 
        style={styles.modalContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          style={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon size={20} color={theme.colors.textSecondary} />
        </button>

        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            About ChordBaker
          </h2>
        </div>

        {/* Scrollable Content */}
        <div style={styles.scrollContainer}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Support & Feedback</h3>
            <p style={styles.text}>
              Hi! I'm Tomás, the developer behind ChordBaker. I'm working hard to make this app great for musicians everywhere.
            </p>
            <p style={styles.text}>
              Please send all questions, suggestions, bug reports, or any other feedback to{' '}
              <span style={styles.email} onClick={handleEmailPress}>
                tomas@chordbaker.com
              </span>
            </p>
            <p style={styles.text}>
              It's just me working on this app, so I really appreciate your patience and feedback to help make ChordBaker better!
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Privacy Policy</h3>
            <p style={styles.text}>
              ChordBaker respects your privacy. We don't collect, store, or share any of your personal data. Your songs and chord progressions stay on your device.
            </p>
            <p style={styles.text}>
              For complete details about our privacy practices, please visit our{' '}
              <span style={styles.link} onClick={handlePrivacyPolicyPress}>
                privacy policy
              </span>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
